import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/app/StatusBadge";
import { Button } from "@/components/ui/button";
import { Laptop2, Wrench, Undo2, Rocket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { own } from "@/lib/terminology";

export default function MyDevices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ employee: null, assets: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/devices").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Employee self-service</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">{own(user, "Devices")}</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Every technology asset currently assigned to you, and quick actions when something isn&apos;t right.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !data.employee ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center">
          <Laptop2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-700 font-medium">No linked employee profile</div>
          <div className="text-xs text-slate-500 mt-1">Ask your IT admin to link your account to an employee record.</div>
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-slate-900 text-amber-400 grid place-items-center font-heading font-bold text-lg">
              {data.employee.name.slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg font-semibold text-slate-900">{data.employee.name}</div>
              <div className="text-xs text-slate-500">{data.employee.email} · {data.employee.department} · {data.employee.country}</div>
            </div>
            <div className="text-xs text-slate-500">Assets assigned</div>
            <div className="font-heading text-2xl font-bold tabular-nums">{data.assets.length}</div>
          </div>

          <div className="space-y-2">
            {data.assets.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-500">
                You don&apos;t have any devices assigned right now.
              </div>
            ) : data.assets.map((a) => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-md p-4 flex flex-col md:flex-row md:items-center gap-4">
                <div className="h-10 w-10 rounded-md bg-slate-100 grid place-items-center">
                  <Laptop2 className="h-5 w-5 text-slate-700" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-base font-semibold text-slate-900">{a.brand} {a.model}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.serial} · {a.asset_tag}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.config}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge value={a.lifecycle_state} />
                  <StatusBadge value={a.condition} />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => navigate("/services")}>
                    <Wrench className="h-4 w-4 mr-1.5" /> Report issue
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <QuickCard icon={Rocket} title="Request a new device" onClick={() => navigate("/services")} desc="Start a Deploy Device request." />
            <QuickCard icon={Wrench} title="Report a problem" onClick={() => navigate("/services")} desc="Open a service case." />
            <QuickCard icon={Undo2} title="Return a device" onClick={() => navigate("/services")} desc="Trigger a reverse pickup." />
          </div>
        </>
      )}
    </div>
  );
}

function QuickCard({ icon: Icon, title, desc, onClick }) {
  return (
    <button onClick={onClick} className="text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 hover:shadow-sm transition-[box-shadow,border-color]">
      <Icon className="h-4 w-4 text-slate-700" strokeWidth={1.6} />
      <div className="mt-2 font-heading font-semibold text-slate-900 text-sm">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
    </button>
  );
}
